/**
 * FILE VALIDATION & SECURITY UTILITIES
 * 
 * Comprehensive client-side validation for file uploads before transmission to backend.
 * Prevents malicious files, corrupted data, and invalid submissions.
 * 
 * WHY: Multiple layers of validation catch issues early and prevent server abuse
 * - Layer 1: Type/extension check (in useFileUpload)
 * - Layer 2: Zero-byte and magic byte verification (this file)
 * - Layer 3: Pre-upload rate limiting (this file)
 * - Layer 4: Hash verification (this file)
 */

/**
 * Magic bytes (file signatures) for common file types
 * WHY: Magic bytes are the first few bytes of file content that identify file type
 * This prevents attackers from renaming files (e.g., malware.exe → malware.pdf)
 * 
 * Format: [magic_bytes_hex]: { mimeType, extension, description }
 */
const MAGIC_BYTES = {
  // PDF files start with %PDF
  '25504446': { mimeType: 'application/pdf', ext: '.pdf', name: 'PDF' },
  
  // JPEG files
  'FFD8FF': { mimeType: 'image/jpeg', ext: '.jpg', name: 'JPEG' },
  
  // PNG files
  '89504E47': { mimeType: 'image/png', ext: '.png', name: 'PNG' },
  
  // TIFF files (little-endian)
  '49492A00': { mimeType: 'image/tiff', ext: '.tif', name: 'TIFF LE' },
  
  // TIFF files (big-endian)
  '4D4D002A': { mimeType: 'image/tiff', ext: '.tif', name: 'TIFF BE' },
  
  // ZIP-based formats (DOCX, XLSX, etc.)
  '504B0304': { mimeType: 'application/zip', ext: '.zip', name: 'ZIP/Office' },
  
  // GZIP
  '1F8B08': { mimeType: 'application/gzip', ext: '.gz', name: 'GZIP' },
};

/**
 * Track recent uploads to prevent rate-limit abuse
 * WHY: Prevents users from uploading same file multiple times in rapid succession
 * 
 * Format: { fileHash: timestamp }
 */
const recentUploadHashes = new Map();
const UPLOAD_COOLDOWN_MS = 2000; // 2 seconds between same file uploads

/**
 * Check if file is empty (0 bytes)
 * 
 * WHY: Empty files are often mistakes or could indicate corruption
 * 
 * @param {File} file - File to check
 * @returns {boolean} - True if file is empty
 */
export const isEmptyFile = (file) => {
  return file.size === 0;
};

/**
 * Extract magic bytes from file for verification
 * 
 * WHY: Need to read file content to verify it matches claimed type
 * 
 * @param {File} file - File to extract magic bytes from
 * @param {number} numBytes - Number of bytes to read (default 4)
 * @returns {Promise<string>} - Hex string of magic bytes
 */
export const extractMagicBytes = async (file, numBytes = 4) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        // Convert first N bytes to hex string
        const arr = new Uint8Array(e.target.result).subarray(0, numBytes);
        const hex = Array.from(arr)
          .map(b => b.toString(16).padStart(2, '0').toUpperCase())
          .join('');
        resolve(hex);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file for magic byte verification'));
      };
      
      // Only read first 4 bytes (enough for most file signatures)
      reader.readAsArrayBuffer(file.slice(0, numBytes));
    } catch (err) {
      reject(new Error(`Error extracting magic bytes: ${err.message}`));
    }
  });
};

/**
 * Verify file's magic bytes match claimed MIME type
 * 
 * WHY: Catches files with spoofed extensions (e.g., malware named as PDF)
 * 
 * @param {File} file - File to verify
 * @param {string} claimedMimeType - MIME type from file.type property
 * @returns {Promise<{valid: boolean, error: string|null}>}
 */
export const verifyMagicBytes = async (file, claimedMimeType) => {
  try {
    // Skip magic byte check for certain MIME types that don't have reliable signatures
    const skipMimeTypes = ['text/plain', 'text/csv', 'application/json', 'text/xml'];
    if (skipMimeTypes.includes(claimedMimeType)) {
      return { valid: true, error: null };
    }

    const magicHex = await extractMagicBytes(file);
    
    // Check if magic bytes match any known type
    for (const [magic, info] of Object.entries(MAGIC_BYTES)) {
      if (magicHex.startsWith(magic)) {
        // Found a matching magic byte signature
        
        // Verify it matches claimed MIME type
        // Allow some flexibility (e.g., application/zip covers DOCX, XLSX, etc.)
        const isCompatible = 
          file.type === info.mimeType ||
          file.type.includes(info.mimeType) ||
          (info.mimeType === 'application/zip' && file.type.includes('openxmlformats'));
        
        if (isCompatible) {
          return { valid: true, error: null };
        }
        
        return {
          valid: false,
          error: `File type mismatch: File appears to be ${info.name} but claimed as ${claimedMimeType}. This could be a security risk.`,
        };
      }
    }
    
    // Magic bytes didn't match known types, but file might be valid
    // Don't block upload, just warn
    console.warn(`[FileValidation] Could not verify magic bytes for ${file.name} (${claimedMimeType})`);
    return { valid: true, error: null };
    
  } catch (err) {
    console.error('[FileValidation] Error verifying magic bytes:', err);
    // Don't block upload due to verification failure
    return { valid: true, error: null };
  }
};

/**
 * Calculate SHA-256 hash of file for integrity verification
 * 
 * WHY: Hash allows detecting file corruption and duplicate uploads
 * 
 * @param {File} file - File to hash
 * @returns {Promise<string>} - Hex string of SHA-256 hash
 */
export const calculateFileHash = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    
    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  } catch (err) {
    throw new Error(`Failed to calculate file hash: ${err.message}`);
  }
};

/**
 * Check if file was recently uploaded (rate limiting)
 * 
 * WHY: Prevents accidental duplicate uploads or prevents abuse by same user
 * 
 * @param {string} fileHash - SHA-256 hash of file
 * @returns {boolean} - True if file was recently uploaded
 */
export const isRecentlyUploaded = (fileHash) => {
  if (!recentUploadHashes.has(fileHash)) {
    return false;
  }
  
  const lastUploadTime = recentUploadHashes.get(fileHash);
  const timeSinceLastUpload = Date.now() - lastUploadTime;
  
  return timeSinceLastUpload < UPLOAD_COOLDOWN_MS;
};

/**
 * Record file as uploaded for rate limiting
 * 
 * @param {string} fileHash - SHA-256 hash of file
 */
export const recordFileUpload = (fileHash) => {
  recentUploadHashes.set(fileHash, Date.now());
  
  // Clean up old entries to prevent memory leak
  // Remove entries older than 1 minute
  const oneMinuteAgo = Date.now() - 60000;
  for (const [hash, time] of recentUploadHashes.entries()) {
    if (time < oneMinuteAgo) {
      recentUploadHashes.delete(hash);
    }
  }
};

/**
 * Sanitize filename to prevent injection attacks
 * 
 * WHY: Filenames could be displayed in UI without proper escaping
 * Remove potentially dangerous characters
 * 
 * @param {string} filename - Original filename
 * @returns {string} - Safe filename for display
 */
export const sanitizeFilename = (filename) => {
  if (!filename) return 'file';
  
  // Remove path components to prevent directory traversal
  let safe = filename.split('\\').pop().split('/').pop();
  
  // Remove null bytes
  safe = safe.replace(/\0/g, '');
  
  // Remove control characters
  // eslint-disable-next-line no-control-regex
  safe = safe.replace(/[\x00-\x1F\x7F]/g, '');
  
  // Limit length to 255 characters (standard filesystem limit)
  safe = safe.substring(0, 255);
  
  return safe || 'file';
};

/**
 * Comprehensive file security validation
 * 
 * WHY: Centralized validation before upload prevents bad files from reaching backend
 * Performs multiple checks:
 * 1. Empty file check
 * 2. Magic byte verification (optional)
 * 3. Rate limiting check
 * 4. Hash calculation
 * 
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.checkMagicBytes - Whether to verify magic bytes (default: true)
 * @param {boolean} options.checkRateLimit - Whether to check rate limiting (default: false)
 * @returns {Promise<{valid: boolean, error: string|null, hash: string|null}>}
 */
export const validateFileSecure = async (file, options = {}) => {
  const { checkMagicBytes = true, checkRateLimit = false } = options;
  
  try {
    // Check 1: Empty file
    if (isEmptyFile(file)) {
      return {
        valid: false,
        error: `File is empty: ${file.name}`,
        hash: null,
      };
    }
    
    // Check 2: Magic bytes verification
    if (checkMagicBytes) {
      const magicCheck = await verifyMagicBytes(file, file.type);
      if (!magicCheck.valid) {
        return {
          valid: false,
          error: magicCheck.error,
          hash: null,
        };
      }
    }
    
    // Check 3 & 4: Calculate hash and check rate limiting
    let fileHash = null;
    if (checkRateLimit) {
      fileHash = await calculateFileHash(file);
      
      if (isRecentlyUploaded(fileHash)) {
        return {
          valid: false,
          error: `File was uploaded recently. Please wait a moment before re-uploading the same file.`,
          hash: fileHash,
        };
      }
      
      // Record this upload
      recordFileUpload(fileHash);
    }
    
    return {
      valid: true,
      error: null,
      hash: fileHash,
    };
    
  } catch (err) {
    console.error('[FileValidation] Security validation error:', err);
    
    return {
      valid: false,
      error: `File validation failed: ${err.message}`,
      hash: null,
    };
  }
};

/**
 * Batch security validation for multiple files
 * 
 * WHY: Validate all files before starting upload process
 * 
 * @param {Array<File>} files - Array of files to validate
 * @param {Object} options - Validation options (passed to validateFileSecure)
 * @returns {Promise<{validFiles: Array, invalidFiles: Array, errors: Array<string>}>}
 */
export const validateFilesSecure = async (files, options = {}) => {
  const validFiles = [];
  const invalidFiles = [];
  const errors = [];
  
  for (const file of files) {
    try {
      const validation = await validateFileSecure(file, options);
      
      if (validation.valid) {
        validFiles.push({
          file,
          hash: validation.hash,
        });
      } else {
        invalidFiles.push(file);
        errors.push(validation.error);
      }
    } catch (err) {
      invalidFiles.push(file);
      errors.push(`Error validating ${file.name}: ${err.message}`);
    }
  }
  
  return { validFiles, invalidFiles, errors };
};

/**
 * Format validation error for user display
 * 
 * @param {string} error - Raw error message
 * @returns {string} - User-friendly error message
 */
export const formatValidationError = (error) => {
  // Make technical errors more user-friendly
  if (error.includes('spoofed')) {
    return 'This file appears to be a different type than its extension suggests. Please verify the file is correct.';
  }
  
  if (error.includes('recently')) {
    return 'Please wait a moment before uploading the same file again.';
  }
  
  if (error.includes('empty')) {
    return 'This file appears to be empty. Please try a different file.';
  }
  
  // Return original error if no pattern matches
  return error;
};

/**
 * Export all validation utilities as default object
 */
const fileValidation = {
  isEmptyFile,
  extractMagicBytes,
  verifyMagicBytes,
  calculateFileHash,
  isRecentlyUploaded,
  recordFileUpload,
  sanitizeFilename,
  validateFileSecure,
  validateFilesSecure,
  formatValidationError,
  MAGIC_BYTES,
};

export default fileValidation;

